import { useCallback } from 'react';

import type {
  ICascaderChangeContextCheck,
  TCascaderChangeContext,
} from '@/pages/datalab/components/MultipleCascader/types';

type TStringPath = string[];

interface ICascaderMenuOption {
  label: React.ReactNode;
  value: string;
  disabled?: boolean;
  children?: ICascaderMenuOption[];
}

type TSecondLevelMap = Record<string, string | undefined>;

const buildSecondLevelMap = (paths: TStringPath[]): TSecondLevelMap => {
  const result: TSecondLevelMap = {};

  paths.forEach((path) => {
    if (!Array.isArray(path) || path.length < 2) return;
    const [level1, level2] = path;
    if (!level1 || !level2) return;
    // 后出现的第二级覆盖之前的，保证“最近一次交互”的结果生效
    result[level1] = level2;
  });

  return result;
};

/**
 * 在“同一级下二级只能选一个”的前提下，对 nextValue 做归一化处理。
 *
 * 需要覆盖的关键业务场景（对应产品描述）：
 * 1）点击一级菜单：
 *    - 期望：默认选中该一级下的第一个二级及其下所有三级，其他二级禁用；
 *    - 要点：避免出现“第一个二级被选中，但最后一个二级也被选中并被禁用”的异常状态。
 *
 * 2）点击某个二级菜单：
 *    - 期望：自动勾选该二级下所有三级，且同一级下其它二级全部禁用；
 *    - 要点：任意时刻，同一级下只允许存在一个“有效的二级”。
 *
 * 3）在场景 2 的基础上，手动取消该二级下所有三级：
 *    - 期望：该二级视为“完全取消选中”，同一级下其它二级应全部解禁；
 *    - 要点：Panel 会保留一条 [level1, level2] 的二级路径，如果不清理，会被误判为仍然选中。
 *
 * 4）点击一级菜单后，默认选中该一级下第一个二级，然后用户手动取消这个二级：
 *    - 期望：该一级下不再有任何选中，所有二级均可被再次勾选，不应自动跳到下一个二级继续勾选。
 *
 * 实现思路：
 * - 第一步：清理“仅剩二级但该二级有子节点且所有三级都被取消”的伪选中状态；
 * - 第二步：基于清理后的值构建 secondLevelMap，并过滤出每个一级下唯一保留的二级。
 */
const normalizeWithSecondLevelConstraint = (
  nextValue: TStringPath[],
  data: ICascaderMenuOption[],
): TStringPath[] => {
  if (!nextValue?.length) {
    return [];
  }

  // 先清理仅存在“二级选中但所有三级已取消”的情况：
  // 对于有子节点的二级，如果只剩下 [level1, level2]，则视为该二级已完全取消选中。
  const cleanedValue = nextValue.filter((path) => {
    if (!Array.isArray(path) || path.length !== 2) {
      return true;
    }

    const [level1, level2] = path;
    if (!level1 || !level2) {
      return true;
    }

    const level1Option = data.find((item) => item.value === level1);
    const level2Options = level1Option?.children || [];
    const targetLevel2 = level2Options.find((item) => item.value === level2);

    // 没有找到对应二级，或者该二级本身就是叶子（没有三级），保留原样
    if (
      !targetLevel2 ||
      !Array.isArray(targetLevel2.children) ||
      !targetLevel2.children.length
    ) {
      return true;
    }

    // 有三级子节点，但当前选中集中没有任何以 [level1, level2] 为前缀且长度 > 2 的路径，
    // 说明用户已经手动取消了所有三级，该二级不再视为选中。
    const hasLeafSelection = nextValue.some((candidate) => {
      if (!Array.isArray(candidate) || candidate.length <= 2) {
        return false;
      }
      return candidate[0] === level1 && candidate[1] === level2;
    });

    return hasLeafSelection;
  });

  const secondLevelMap = buildSecondLevelMap(cleanedValue);

  const constrainedValue = cleanedValue.filter((path) => {
    if (!Array.isArray(path) || path.length < 2) return true;
    const [level1, level2] = path;
    const chosenLevel2 = secondLevelMap[level1];
    if (!chosenLevel2) return true;
    return level2 === chosenLevel2;
  });

  return constrainedValue;
};

/**
 * 基于「点击一级菜单」这一特殊交互，对 nextValue 做一次“重建”：
 *
 * 目标场景：
 * - 当用户勾选某个一级菜单时：
 *   1）默认选中该一级下的第一个二级；
 *   2）自动勾选该二级下所有三级；
 *   3）清理该一级下原有的所有选中路径，防止和“默认选中的首个二级”混在一起。
 *
 * 为什么需要单独处理：
 * - MultipleCascaderPanel 在勾选一级时，会生成：
 *   - 一级路径：[level1]
 *   - 以及所有叶子路径：[level1, level2, level3...]（可能跨多个二级）；
 * - 如果直接把这些值交给 normalizeWithSecondLevelConstraint，会出现：
 *   - “第一个二级 + 其它二级的叶子”同时存在，导致你看到的
 *     “默认勾选了第一个二级，又勾选了最后一个二级且被禁用”的异常状态。
 *
 * 因此，这里在归一化前，显式将“勾选一级”重写为
 *   「清空该一级下历史选中」+「首个二级 + 其所有三级」。
 */
const resolveValueWithFirstLevelClick = (
  nextValue: TStringPath[],
  context: TCascaderChangeContext,
  data: ICascaderMenuOption[],
): TStringPath[] => {
  if (!context || typeof context !== 'object') {
    return nextValue;
  }

  if (context.type !== 'check') {
    return nextValue;
  }

  const ctx = context as ICascaderChangeContextCheck;
  const { path } = ctx;

  // 仅在“勾选一级菜单”时进行特殊处理
  if (!Array.isArray(path) || path.length !== 1) {
    return nextValue;
  }

  const [level1] = path;
  if (!level1) {
    return nextValue;
  }

  const level1Option = data.find((item) => item.value === level1);
  const level2Options = level1Option?.children || [];
  if (!level2Options.length) {
    return nextValue;
  }

  const firstLevel2 = level2Options[0];
  const level3Options = firstLevel2.children || [];

  // 移除该一级下原有的所有选中路径，防止旧的二级/三级与“默认首个二级”混在一起
  const others = nextValue.filter(
    (pathItem) => !Array.isArray(pathItem) || pathItem[0] !== level1,
  );

  const expanded: TStringPath[] = [];

  if (Array.isArray(level3Options) && level3Options.length > 0) {
    level3Options.forEach((level3) => {
      expanded.push([level1, firstLevel2.value, level3.value]);
    });
  } else {
    expanded.push([level1, firstLevel2.value]);
  }

  return [...others, ...expanded];
};

/**
 * ABTestCascader 的 onChange 业务处理逻辑。
 * 说明：此 Hook 封装了“第二级单选约束”和受控 / 非受控 value 更新策略，
 * 仅供 ABTestCascader 组合使用。
 */
const useAbTestCascaderOnChange = (
  data: ICascaderMenuOption[],
  enableSecondLevelSingleSelect: boolean,
  value: TStringPath[] | undefined,
  onChange: ((value: TStringPath[], context: TCascaderChangeContext) => void) | undefined,
  setInnerValue: React.Dispatch<React.SetStateAction<TStringPath[]>>,
) => {
  return useCallback(
    (nextValue: TStringPath[], context: TCascaderChangeContext) => {
      let resolvedValue = nextValue;

      if (enableSecondLevelSingleSelect) {
        /**
         * 第一步：处理“点击一级菜单”的特殊逻辑。
         *
         * 对应业务场景：
         * - 点击一级菜单时，默认选中该一级下的第一个二级及其所有三级，
         *   并清空该一级下历史选中，避免“多个二级同时选中”的问题。
         */
        resolvedValue = resolveValueWithFirstLevelClick(
          nextValue,
          context,
          data,
        );
      }

      const constrainedValue = enableSecondLevelSingleSelect
        ? normalizeWithSecondLevelConstraint(resolvedValue, data)
        : resolvedValue;

      if (!value) {
        setInnerValue(constrainedValue);
      }

      onChange?.(constrainedValue, context);
    },
    [data, enableSecondLevelSingleSelect, onChange, value, setInnerValue],
  );
};

export default useAbTestCascaderOnChange;

